
import React from 'react';
import { FlowNode, Language } from '../../types';

interface FlowsInspectorProps {
  language: Language;
  showInspector: boolean;
  selectedNode: FlowNode | undefined;
  onUpdateNodeLabel: (val: string) => void;
  onUpdateNodeConfig: (key: string, value: any) => void;
  onCloseInspector: () => void;
  onDeleteNode: (id: string) => void;
}

const FlowsInspector: React.FC<FlowsInspectorProps> = ({ 
  language, showInspector, selectedNode, onUpdateNodeLabel, onUpdateNodeConfig, onCloseInspector, onDeleteNode 
}) => {
  const isRtl = language === 'ar';

  if (!selectedNode) return null;

  const renderConfigInput = (label: string, key: string, placeholder: string = '', type: string = 'text') => (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">{label}</label>
      <input 
        type={type}
        value={selectedNode.data.config[key] || ''} 
        onChange={(e) => onUpdateNodeConfig(key, e.target.value)} 
        className="w-full bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-xs font-bold text-white outline-none focus:border-emerald-500 focus:bg-white/5 transition-all"
        placeholder={placeholder}
      />
    </div>
  );

  const renderConfigSelect = (label: string, key: string, options: string[]) => (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">{label}</label>
      <select 
        value={selectedNode.data.config[key] || options[0]} 
        onChange={(e) => onUpdateNodeConfig(key, e.target.value)} 
        className="w-full bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-xs font-bold text-white outline-none focus:border-emerald-500 focus:bg-white/5 transition-all appearance-none"
      >
        {options.map(opt => <option key={opt} value={opt} className="bg-[#1a1a20]">{opt}</option>)}
      </select>
    </div>
  );

  const renderConfigTextarea = (label: string, key: string, placeholder: string = '') => (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">{label}</label>
      <textarea 
        value={selectedNode.data.config[key] || ''} 
        onChange={(e) => onUpdateNodeConfig(key, e.target.value)} 
        className="w-full bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-xs font-mono text-emerald-400 outline-none focus:border-emerald-500 focus:bg-white/5 transition-all h-24 resize-none"
        placeholder={placeholder}
      />
    </div>
  );

  const renderSpecificConfig = () => {
    // Determine the type logic (handling specific subtypes if defined in config or base type)
    const type = selectedNode.data.config.type || selectedNode.type;

    switch (type) {
      case 'webhook':
        return (
          <div className="p-4 bg-emerald-500/5 rounded-xl border border-emerald-500/20 space-y-3">
             <div className="flex items-center gap-2 text-emerald-500 mb-1">
                <i className="fas fa-bolt"></i>
                <span className="text-xs font-black">Webhook Configuration</span>
             </div>
             <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-slate-500 uppercase">Method</label>
                <div className="text-xs font-mono text-white bg-black/40 px-3 py-2 rounded-lg border border-white/5">POST</div>
             </div>
             <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-slate-500 uppercase">Endpoint URL</label>
                <div className="flex items-center gap-2 bg-black/40 px-3 py-2 rounded-lg border border-white/5">
                   <span className="text-[10px] font-mono text-slate-400 truncate flex-1">https://api.lahja.app/hooks/{selectedNode.id}</span>
                   <button className="text-emerald-500 hover:text-white"><i className="fas fa-copy"></i></button>
                </div>
             </div>
          </div>
        );
      
      case 'http':
        return (
          <div className="space-y-4">
             {renderConfigSelect('Method', 'method', ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'])}
             {renderConfigInput('URL Endpoint', 'url', 'https://api.example.com/v1/resource')}
             {renderConfigTextarea('Headers (JSON)', 'headers', '{"Authorization": "Bearer..."}')}
             {renderConfigTextarea('Body (JSON)', 'body', '{"key": "value"}')}
          </div>
        );

      case 'condition':
        return (
          <div className="space-y-4">
             {renderConfigInput('Variable Name', 'variable', 'user_response')}
             {renderConfigSelect('Operator', 'operator', ['Equals', 'Contains', 'Greater Than', 'Less Than', 'Is Empty', 'Is Not Empty'])}
             {renderConfigInput('Value', 'value', 'yes')}
          </div>
        );

      case 'email':
        return (
          <div className="space-y-4">
             {renderConfigInput('Recipient (To)', 'to', 'client@example.com')}
             {renderConfigInput('Subject', 'subject', 'Meeting Confirmation')}
             {renderConfigTextarea('Message Body', 'body', 'Hello, ...')}
          </div>
        );

      case 'ai_service':
        return (
          <div className="space-y-4">
             <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/20 flex items-start gap-3">
                <i className="fas fa-robot text-blue-500 mt-1"></i>
                <div>
                   <h4 className="text-xs font-bold text-white">AI Processing</h4>
                   <p className="text-[10px] text-slate-400">This node uses the selected service configuration.</p>
                </div>
             </div>
             {renderConfigTextarea('System Instruction Override', 'system_prompt', 'Leave empty to use service default...')}
             {renderConfigSelect('Model', 'model', ['gemini-pro', 'gemini-flash'])}
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-4">
             {renderConfigSelect('Frequency', 'frequency', ['Every Hour', 'Every Day', 'Every Week', 'Custom Cron'])}
             {renderConfigInput('Time (UTC)', 'time', '09:00', 'time')}
          </div>
        );

      case 'script':
        return (
          <div className="space-y-2">
             <div className="flex justify-between items-center">
               <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">JavaScript Code</label>
               <span className="text-[9px] text-amber-500"><i className="fas fa-exclamation-triangle"></i> Advanced</span>
             </div>
             <textarea 
                value={selectedNode.data.config.code || '// return { result: true };'} 
                onChange={(e) => onUpdateNodeConfig('code', e.target.value)} 
                className="w-full bg-[#0a0a0c] border border-white/10 rounded-xl p-3 text-xs font-mono text-pink-400 outline-none focus:border-emerald-500 transition-all h-48 resize-none"
                spellCheck={false}
             />
          </div>
        );

      default:
        return (
          <div className="text-center py-8 opacity-50">
             <i className="fas fa-sliders text-2xl mb-2 text-slate-600"></i>
             <p className="text-[10px] font-bold text-slate-500">No specific configuration for this node type.</p>
          </div>
        );
    }
  };

  return (
    <aside className={`absolute lg:static inset-y-0 ${isRtl ? 'left-0' : 'right-0'} w-80 bg-[#15151a] border-l border-white/5 z-30 transition-transform duration-300 flex flex-col shadow-2xl ${showInspector && selectedNode ? 'translate-x-0' : 'translate-x-full'}`}>
       {selectedNode && (
          <>
             {/* Header */}
             <div className="h-16 px-6 border-b border-white/5 flex items-center justify-between bg-[#1a1a20]">
                <h3 className="text-xs font-black uppercase text-white tracking-widest flex items-center gap-2">
                   <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                   Inspector
                </h3>
                <button onClick={onCloseInspector} className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                   <i className="fas fa-times"></i>
                </button>
             </div>

             <div className="flex-1 overflow-y-auto custom-scrollbar">
                {/* General Section */}
                <div className="p-6 border-b border-white/5 space-y-5">
                   <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-4">General Properties</h4>
                   
                   <div className="space-y-1.5">
                      <label className="text-[9px] font-bold text-slate-500 uppercase block">Node ID</label>
                      <div className="font-mono text-[10px] text-slate-400 break-all select-all bg-black/20 p-2 rounded-lg border border-white/5">
                         {selectedNode.id}
                      </div>
                   </div>

                   <div className="space-y-1.5">
                      <label className="text-[9px] font-bold text-slate-500 uppercase block">Label</label>
                      <input 
                         type="text" 
                         value={selectedNode.data.label} 
                         onChange={(e) => onUpdateNodeLabel(e.target.value)} 
                         className="w-full bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-sm font-bold text-white outline-none focus:border-emerald-500 transition-colors" 
                      />
                   </div>
                   
                   <div className="space-y-1.5">
                      <label className="text-[9px] font-bold text-slate-500 uppercase block">Description</label>
                      <textarea 
                         value={selectedNode.data.config.description || ''} 
                         onChange={(e) => onUpdateNodeConfig('description', e.target.value)}
                         className="w-full bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-slate-300 outline-none focus:border-emerald-500 transition-colors h-16 resize-none" 
                         placeholder="Add a note about this step..." 
                      />
                   </div>
                </div>

                {/* Specific Configuration Section */}
                <div className="p-6">
                   <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-4 flex items-center justify-between">
                      <span>Configuration</span>
                      <i className="fas fa-cog opacity-50"></i>
                   </h4>
                   {renderSpecificConfig()}
                </div>
             </div>

             {/* Footer Actions */}
             <div className="p-4 border-t border-white/5 bg-[#1a1a20]">
                <button onClick={() => onDeleteNode(selectedNode.id)} className="w-full py-3 bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl text-xs font-black transition-all border border-rose-500/20 hover:border-rose-500 flex items-center justify-center gap-2 group">
                   <i className="fas fa-trash group-hover:animate-bounce"></i> Delete Node
                </button>
             </div>
          </>
       )}
    </aside>
  );
};
export default FlowsInspector;
